import * as THREE from 'three';
import { AbstractProjectile } from './AbstractProjectile';
import { LODLevel } from '../optimization/LODSystem';
import { SpatialManager } from '../optimization/SpatialManager';
import { GameObject } from '../objects/GameObject';
import { Damageable } from './ProjectileManager';

/**
 * RapidFireProjectile implementation - high rate of fire, low damage projectile for automatic weapons
 * Similar to Quake 3 Arena machine gun/plasma gun style projectile
 */
export class RapidFireProjectile extends AbstractProjectile {
  private radius: number = 0.035; // Smaller radius for better appearance
  private collisionRadius: number = 0.15; // Match BasicProjectile collision radius
  private spatialManager: SpatialManager | null = null;
  
  /**
   * Creates a new RapidFireProjectile
   * @param scene The scene to add the mesh to
   */
  constructor(scene: THREE.Scene) {
    super(scene);
    this.damage = 50; // Higher damage for testing
    this.lifetime = 2.0; // Shorter lifetime for better performance
    
    // Create meshes for different LOD levels
    this.createLODMeshes();
  }
  
  /**
   * Set the spatial manager for collision detection
   */
  public setSpatialManager(spatialManager: SpatialManager): void {
    this.spatialManager = spatialManager;
    console.log('RapidFireProjectile: SpatialManager set');
  }
  
  /**
   * Creates the high, medium, and low detail meshes
   */
  private createLODMeshes(): void {
    // Use bright blue color for better visibility and to differentiate from tower projectiles
    const mainColor = 0x33aaff;  // Bright blue
    const emissiveColor = 0x0066ff; // Blue glow
    const emissiveIntensity = 1.5; // Brighter glow than basic projectile
    
    // Create material with glow effect for better visibility
    const material = new THREE.MeshStandardMaterial({ 
      color: mainColor,
      emissive: emissiveColor,
      emissiveIntensity: emissiveIntensity,
      metalness: 0.7, // More metallic look
      roughness: 0.1, // Smoother finish
      flatShading: true
    });
    
    // HIGH detail mesh - using cube geometry instead of cylinder
    const highGeometry = new THREE.BoxGeometry(this.radius * 1.5, this.radius * 1.5, this.radius * 3);
    const highMesh = new THREE.Mesh(highGeometry, material);
    this.lodMeshes.set(LODLevel.HIGH, highMesh);
    
    // MEDIUM detail mesh - slightly simpler cube
    const medGeometry = new THREE.BoxGeometry(this.radius * 1.5, this.radius * 1.5, this.radius * 3);
    const medMesh = new THREE.Mesh(medGeometry, material);
    this.lodMeshes.set(LODLevel.MEDIUM, medMesh);
    
    // LOW detail mesh - even simpler cube
    const lowGeometry = new THREE.BoxGeometry(this.radius * 1.5, this.radius * 1.5, this.radius * 3);
    const lowMesh = new THREE.Mesh(lowGeometry, material);
    this.lodMeshes.set(LODLevel.LOW, lowMesh);
    
    // Set the high detail mesh as the default
    this.mesh = highMesh;
  }
  
  /**
   * Creates the mesh for this projectile
   */
  public createMesh(): void {
    if (!this.mesh) {
      this.createLODMeshes();
    }
  }
  
  /**
   * Update the projectile
   * @param deltaTime Time since last update in seconds
   * @returns Whether the projectile is still active
   */
  public override update(deltaTime: number): boolean {
    // Call parent update - handles basic movement
    if (!super.update(deltaTime)) {
      return false;
    }
    
    // Check collisions - same as BasicProjectile
    this.checkCollisions();
    
    return this.active;
  }
  
  /**
   * Perform a simple collision check
   */
  private checkCollisions(): void {
    try {
      // Check collision with floor
      if (this.position.y <= this.radius) {
        // Hit the floor
        this.onImpact({ type: 'floor', position: new THREE.Vector3(this.position.x, 0, this.position.z) });
        return; // End collision check if we hit the floor
      }
      
      // If we have a spatial manager, use it for more efficient collision detection
      if (this.spatialManager) {
        // Use a larger radius for detection to ensure we don't miss objects
        const detectionRadius = this.collisionRadius * 10;
        
        // Get nearby objects within collision radius
        const nearbyObjects = this.spatialManager.getObjectsInRadius(
          this.position,
          detectionRadius
        );
        
        if (nearbyObjects.length > 0) {
          console.log(`RapidFireProjectile: Found ${nearbyObjects.length} objects within radius ${detectionRadius.toFixed(2)}`);
          
          // Log object types detected
          console.log('Object types found:', nearbyObjects.map(obj => {
            // Safely check if object has takeDamage method
            const isDamageable = obj && typeof (obj as any).takeDamage === 'function';
            return `${obj.constructor.name}(damageable: ${isDamageable})`;
          }).join(', '));
        }
        
        // Check for collisions with each nearby object
        for (const object of nearbyObjects) {
          try {
            // Check if this object is Damageable (debug) - safely cast
            const hasTakeDamage = object && typeof (object as any).takeDamage === 'function';
            console.log(`Checking object ${object.constructor.name} - Has takeDamage: ${hasTakeDamage}`);
            
            if (this.collidesWithGameObject(object)) {
              console.log(`🔥 COLLISION: RapidFireProjectile hit ${object.constructor.name}!`);
              this.onImpact(object);
              return; // End collision check after first hit
            }
          } catch (collisionError) {
            console.error(`Error during collision check with ${object.constructor.name}:`, collisionError);
          }
        }
      } else {
        console.warn('RapidFireProjectile: No spatial manager available for collision detection');
      }
    } catch (error) {
      console.error('Error in projectile collision detection:', error);
    }
  }
  
  /**
   * Check if this projectile collides with a GameObject
   */
  private collidesWithGameObject(object: GameObject): boolean {
    // Get positions
    const objectPosition = object.getPosition();
    const projectilePosition = this.position.clone();
    
    // Calculate distance
    const distanceSquared = projectilePosition.distanceToSquared(objectPosition);
    const distance = Math.sqrt(distanceSquared);
    
    // Get object's scale to estimate collision radius
    const objectScale = object.getScale();
    const objectRadius = Math.max(objectScale.x, objectScale.y, objectScale.z) * 0.5;
    
    // Combined collision distance
    const collisionDistance = this.collisionRadius + objectRadius;
    
    // Check if distance is within collision range - use distance not distanceSquared
    // This allows for direct comparison with collisionDistance
    const collision = distance <= collisionDistance;
    
    console.log(`Collision check with ${object.constructor.name}: Distance=${distance.toFixed(2)}, Threshold=${collisionDistance.toFixed(2)}, Result=${collision}`);
    
    return collision;
  }
  
  /**
   * Handle projectile impact with a target
   * @param target The object that was hit
   */
  public onImpact(target: any): void {
    // Check if target has the expected function
    const canTakeDamage = target && typeof target.takeDamage === 'function';
    
    console.log(`🎯 RapidFireProjectile impact with ${target?.constructor?.name || typeof target}`);
    console.log(`Can take damage: ${canTakeDamage}, Damage value: ${this.damage}`);
    
    // Apply damage if target implements Damageable
    if (canTakeDamage) {
      try {
        console.log(`Calling takeDamage(${this.damage}) on ${target.constructor.name}`);
        target.takeDamage(this.damage);
        console.log(`✅ Successfully applied ${this.damage} damage`);
      } catch (error) {
        console.error(`Failed to apply damage to ${target.constructor.name}:`, error);
      }
    } else {
      console.warn(`Target doesn't implement takeDamage: ${target?.constructor?.name || typeof target}`);
    }
    
    // Deactivate the projectile
    this.deactivate();
  }
  
  /**
   * Get the collision radius for this projectile
   */
  public getCollisionRadius(): number {
    return this.collisionRadius;
  }
} 